import * as crypto from 'crypto';
import base64url from 'base64url';
import Dictionaries from './dictionaries.service';
import { Injectable } from '@angular/core';
import { UserCredential } from './webauthn.service';

interface PubKeyCredParams {
  type: string;
  alg: number;
}

export interface AttestationChallenge {
  user: {
    id: string | Buffer;
    name: string;
    displayName: string;
  };
  attestation: string;
  authenticatorSelection: {
    authenticatorAttachment: string;
    userVerification: string;
  };
  rp: {
    name?: string;
  };
  pubKeyCredParams: PubKeyCredParams[];
  status: string;
  challenge: string | Buffer | undefined;
  session: {
    challenge: string | Buffer | undefined;
    name: string;
    loggedIn?: boolean;
  };
}

@Injectable({
  providedIn: 'root',
})
export default class AttestationChallengeBuilder {
  private readonly result: Partial<AttestationChallenge> = {};

  constructor(private readonly dictionaries: Dictionaries) {}

  public setRelyingPartyInfo(relyingParty: { name: string } | null): this {
    if (!relyingParty) {
      this.result.rp = {};
    } else {
      this.result.rp = { ...relyingParty };
    }

    return this;
  }

  public setUserInfo(user: UserCredential): this {
    if (!user.id) {
      throw new Error('id required');
    }

    if (!user.name) {
      throw new Error(`name required`);
    }

    this.result.user = {
      id: user.id,
      name: user.name,
      displayName: user.name,
    };

    return this;
  }

  public addCredentialRequest(value: PubKeyCredParams | PubKeyCredParams[]): this {
    const { PublicKeyCredentialType } = this.dictionaries;
    let { pubKeyCredParams = [] } = this.result;

    if (!Array.isArray(pubKeyCredParams)) {
      pubKeyCredParams = [pubKeyCredParams];
    }

    if (Array.isArray(value)) {
      value.forEach((option) => this.addCredentialRequest(option));
      return this;
    }

    const { type, alg } = value;

    if (!type || !alg || !Object.values(PublicKeyCredentialType).includes(type)) {
      throw new Error('Invalid PublicKeyCredentialParameters.');
    }

    pubKeyCredParams.push({ type, alg });

    this.result.pubKeyCredParams = pubKeyCredParams;
    return this;
  }

  public setAuthenticator(value: { authenticatorAttachment: string; userVerification: string }): this {
    const { authenticatorSelection = {} } = this.result;
    const values = Object.values(this.dictionaries.AuthenticatorAttachment);

    if (!values.includes(value.authenticatorAttachment)) {
      throw new Error(`Invalid AuthenticatorAttachment value. Must be one of "${values.join('", "')}".`);
    }

    this.result.authenticatorSelection = {
      ...authenticatorSelection,
      ...value,
    };

    return this;
  }

  public setAttestationType(attestation?: AttestationConveyancePreference): this {
    if (!attestation) {
      this.result.attestation = '';

      return this;
    }
    this.result.attestation = attestation;
    return this;
  }

  public build(override = {}): Partial<AttestationChallenge> {
    const challenge = base64url.encode(crypto.randomBytes(32).toString('hex'));
    const { rp, user, attestation, pubKeyCredParams } = this.result;

    if (!rp) {
      throw new Error('Requires RP information');
    }

    if (!user) {
      throw new Error('Requires user information');
    }

    if (!attestation) {
      this.setAttestationType();
    }

    if (!pubKeyCredParams || !pubKeyCredParams.length) {
      this.addCredentialRequest({ type: this.dictionaries.PublicKeyCredentialType.PUBLIC_KEY, alg: -7 });
    }

    const result = { ...this.result, ...override, challenge };

    return result;
  }
}
